/**
 * Compact Config Panel - merges old ConfigCards + AdvancedFilters
 * Single collapsible row with all settings
 */
import React, { useState } from 'react';
import { Box, Typography, Slider, Switch, Collapse, alpha } from '@mui/material';
import { TuneOutlined, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const MiniSlider = ({ label, value, displayValue, unit, min, max, step, color, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, flex: 1 }}>
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: '0.82rem', fontWeight: 900, color: color || '#3b82f6', fontFamily: 'monospace', minWidth: 22, textAlign: 'right' }}>
      {displayValue}
    </Typography>
    <Slider
      value={value}
      onChange={(e, v) => onChange(v)}
      min={min} max={max} step={step}
      size="small"
      sx={{
        flex: 1, minWidth: 50,
        '& .MuiSlider-thumb': { width: 12, height: 12, bgcolor: color || '#3b82f6' },
        '& .MuiSlider-track': { bgcolor: color || '#3b82f6' },
        '& .MuiSlider-rail': { bgcolor: alpha(color || '#3b82f6', 0.15) },
      }}
    />
    {unit && <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#94a3b8', whiteSpace: 'nowrap' }}>{unit}</Typography>}
  </Box>
);

const MiniToggle = ({ label, checked, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
    <Typography sx={{ fontSize: '0.68rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{label}</Typography>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} size="small" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#3b82f6' } }} />
  </Box>
);

const ConfigPanel = ({ config, onConfigChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{
      mb: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff',
      overflow: 'hidden',
    }}>
      {/* Toggle header */}
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.8,
          px: 1.5, py: 0.8, cursor: 'pointer',
          '&:hover': { bgcolor: '#f8fafc' },
          transition: 'background 0.15s',
        }}
      >
        <TuneOutlined sx={{ fontSize: 16, color: '#64748b' }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
          {t('downshift.filters.title', 'Cấu hình & Bộ lọc')}
        </Typography>
        <ExpandMore sx={{
          fontSize: 18, color: '#94a3b8',
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }} />
      </Box>

      <Collapse in={open}>
        <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
          {/* Row 1: Threshold sliders */}
          <Box sx={{ display: 'flex', gap: 3, mb: 1, flexWrap: 'wrap' }}>
            <MiniSlider
              label={t('downshift.config.distribution_threshold.title', 'Ngưỡng phân phối')}
              value={config.distribution_threshold}
              displayValue={config.distribution_threshold}
              min={0} max={100} step={5} color="#dc2626"
              onChange={(v) => onConfigChange({ ...config, distribution_threshold: v })}
            />
            <MiniSlider
              label={t('downshift.config.accumulation_threshold.title', 'Ngưỡng tích lũy')}
              value={config.accumulation_threshold}
              displayValue={config.accumulation_threshold}
              min={0} max={100} step={5} color="#16a34a"
              onChange={(v) => onConfigChange({ ...config, accumulation_threshold: v })}
            />
          </Box>

          {/* Row 2: Filters + Toggles */}
          <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <MiniSlider
              label={t('downshift.filters.liquidity', 'Thanh khoản')}
              value={config.min_avg_value_20 / 1e9}
              displayValue={`${(config.min_avg_value_20 / 1e9).toFixed(0)}`}
              unit={t('downshift.filters.billion', 'tỷ')}
              min={1} max={50} step={1}
              onChange={(v) => onConfigChange({ ...config, min_avg_value_20: v * 1e9 })}
            />
            <MiniSlider
              label={t('downshift.filters.price', 'Giá tối thiểu')}
              value={config.min_price / 1000}
              displayValue={`${(config.min_price / 1000).toFixed(0)}`}
              unit={t('downshift.filters.thousand', 'nghìn')}
              min={1} max={50} step={1}
              onChange={(v) => onConfigChange({ ...config, min_price: v * 1000 })}
            />
            <MiniSlider
              label={t('downshift.filters.show', 'Hiển thị')}
              value={config.limit}
              displayValue={config.limit}
              unit={t('downshift.filters.stocks', 'mã')}
              min={10} max={100} step={10}
              onChange={(v) => onConfigChange({ ...config, limit: v })}
            />

            <Box sx={{ borderLeft: '1px solid #e2e8f0', height: 24 }} />

            <MiniToggle
              label={t('downshift.filters.weeklyConfirm', 'Xác nhận tuần')}
              checked={config.require_weekly_confirmation}
              onChange={(v) => onConfigChange({ ...config, require_weekly_confirmation: v })}
            />
            <MiniToggle
              label={t('downshift.filters.volDivergence', 'Vol phân kỳ')}
              checked={config.use_obv_divergence}
              onChange={(v) => onConfigChange({ ...config, use_obv_divergence: v })}
            />
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ConfigPanel;
